import { useMemo } from 'react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';

import { readShigoAppConfig } from '../../../lib/config';
import { useShigoSession } from '../../../lib/useShigoSession';
import { createIntegrationsClient } from './api';
import type {
  IntegrationMappingType,
  IntegrationMutationPayload,
  IntegrationsFilter,
} from './model';

export const integrationsQueryKey = ['demand-integrations'] as const;
export const integrationsListsQueryKey = [...integrationsQueryKey, 'list'] as const;
export const integrationsByIdsQueryKey = [...integrationsQueryKey, 'by-ids'] as const;
export const integrationStatusCountsQueryKey = [...integrationsQueryKey, 'status-counts'] as const;
export const integrationDetailsQueryKey = [...integrationsQueryKey, 'detail'] as const;

export function useIntegrationsClient() {
  const { session } = useShigoSession();
  const config = readShigoAppConfig();
  if (!session) throw new Error('Shigo session is required.');
  return useMemo(
    () =>
      createIntegrationsClient({
        config: { apiBaseUrl: config.apiBaseUrl, networkId: config.networkId },
        accessToken: session.accessToken,
      }),
    [config.apiBaseUrl, config.networkId, session.accessToken],
  );
}

export function useIntegrationsQuery(filter: IntegrationsFilter, demandPartnerId?: string) {
  const client = useIntegrationsClient();
  return useQuery({
    queryKey: [...integrationsListsQueryKey, demandPartnerId ?? 'global', filter],
    queryFn: () =>
      demandPartnerId ? client.listForPartner(demandPartnerId, filter) : client.list(filter),
  });
}

export function useIntegrationsByIdsQuery(ids: string[]) {
  const client = useIntegrationsClient();
  return useQuery({
    queryKey: [...integrationsByIdsQueryKey, ids],
    queryFn: () => client.listByIds(ids),
    enabled: ids.length > 0,
  });
}

export function useIntegrationStatusCountsQuery(
  searchTerm: string,
  demandPartnerId?: string,
  enabled = true,
) {
  const client = useIntegrationsClient();
  return useQuery({
    queryKey: [...integrationStatusCountsQueryKey, demandPartnerId ?? 'global', searchTerm],
    enabled,
    queryFn: () =>
      demandPartnerId
        ? client.getStatusCountsForPartner(demandPartnerId, { searchTerm })
        : client.getStatusCounts({ searchTerm }),
  });
}

export function useIntegrationQuery(demandPartnerId: string, integrationId: string) {
  const client = useIntegrationsClient();
  return useQuery({
    queryKey: [...integrationDetailsQueryKey, demandPartnerId, integrationId],
    queryFn: () => client.get(demandPartnerId, integrationId),
    enabled: Boolean(demandPartnerId && integrationId),
    refetchOnMount: 'always',
  });
}

export function useIntegrationMutations() {
  const client = useIntegrationsClient();
  const queryClient = useQueryClient();
  const invalidateListsAndCounts = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: integrationsListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: integrationStatusCountsQueryKey }),
      queryClient.invalidateQueries({
        queryKey: integrationsByIdsQueryKey,
        refetchType: 'none',
      }),
    ]);
  const invalidateMappings = () =>
    Promise.all([
      queryClient.invalidateQueries({ queryKey: integrationsListsQueryKey }),
      queryClient.invalidateQueries({ queryKey: integrationStatusCountsQueryKey }),
      queryClient.invalidateQueries({
        queryKey: integrationsByIdsQueryKey,
        refetchType: 'none',
      }),
      queryClient.invalidateQueries({ queryKey: integrationDetailsQueryKey }),
    ]);

  return {
    // react-doctor-disable-next-line react-doctor/query-mutation-missing-invalidation -- read-only detail fetch, writes no server state
    get: useMutation({
      mutationFn: ({
        demandPartnerId,
        integrationId,
      }: {
        demandPartnerId: string;
        integrationId: string;
      }) => client.get(demandPartnerId, integrationId),
    }),
    create: useMutation({
      mutationFn: ({
        demandPartnerId,
        payload,
      }: {
        demandPartnerId: string;
        payload: IntegrationMutationPayload;
      }) => client.create(demandPartnerId, payload),
      onSuccess: invalidateListsAndCounts,
    }),
    update: useMutation({
      mutationFn: ({
        demandPartnerId,
        integrationId,
        payload,
      }: {
        demandPartnerId: string;
        integrationId: string;
        payload: IntegrationMutationPayload;
      }) => client.update(demandPartnerId, integrationId, payload),
      onSuccess: invalidateListsAndCounts,
    }),
    downloadMappingTemplate: useMutation({
      mutationFn: (type: IntegrationMappingType) => client.downloadMappingTemplate(type),
    }),
    uploadMappings: useMutation({
      mutationFn: ({
        integrationId,
        type,
        file,
      }: {
        integrationId: string;
        type: IntegrationMappingType;
        file: File;
      }) => client.uploadMappings(integrationId, type, file),
      onSuccess: invalidateMappings,
    }),
  };
}
